import React, { useEffect, useState } from "react";
import { ReactComponent as StatsImage } from "../../assets/svg/Landing/StatsImage.svg";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const AnimatedCounter = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      const end = parseInt(value.replace(/,/g, ""));

      // For large numbers, use a different approach
      const startValue = end > 100 ? Math.floor(end * 0.1) : 0; // Start at 10% for large numbers
      let start = startValue;

      // Calculate step size based on number magnitude
      const steps = 50; // Fixed number of steps
      const increment = Math.ceil((end - startValue) / steps);

      // Calculate time between increments
      const incrementTime = (duration * 1000) / steps;

      const timer = setInterval(() => {
        start += increment;
        // Ensure we don't exceed the target value
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, incrementTime);

      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  // Format the number with commas for thousands
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return <span ref={ref}>{formatNumber(count)}+</span>;
};

const StatsSection = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Animation variants remain unchanged
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const statsItem = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        duration: 0.5,
      },
    },
  };

  return (
    // JSX remains unchanged
    <motion.section
      ref={sectionRef}
      className="bg-blue-800 text-white relative overflow-hidden"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeIn}
    >
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          <motion.div
            className="w-full lg:w-1/2 flex flex-col items-center lg:items-start relative py-12 px-4 sm:px-6 md:px-0"
            variants={fadeIn}
          >
            <motion.div
              className="w-full flex items-center mb-8 sm:mb-12"
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div
                className="hidden sl:block border-white bg-white border-2 w-16 sm:w-20 md:w-28 h-6 xs:h-8 md:h-10 mr-4"
                initial={{ width: 0 }}
                animate={isInView ? { width: "7rem" } : { width: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
              <motion.h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-center lg:text-left"
                variants={fadeIn}
              >
                What have we done?
              </motion.h2>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 w-full md:px-16"
              variants={staggerContainer}
            >
              <motion.div
                className="text-center lg:text-left p-6 rounded-lg bg-blue-700/30 hover:bg-blue-700/50 transition-colors duration-300"
                variants={statsItem}
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-3xl sm:text-4xl font-bold mb-2">
                  <AnimatedCounter value="4000" />
                </h3>
                <p className="text-sm sm:text-base text-blue-100 md:text-xl">
                  Courses to
                  <br />
                  choose from
                </p>
              </motion.div>

              <motion.div
                className="text-center lg:text-left p-6 rounded-lg bg-blue-700/30 hover:bg-blue-700/50 transition-colors duration-300"
                variants={statsItem}
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-3xl sm:text-4xl font-bold mb-2">
                  <AnimatedCounter value="10000" />
                </h3>
                <p className="text-sm sm:text-base text-blue-100 md:text-xl">
                  Students
                  <br />
                  Trained
                </p>
              </motion.div>

              <motion.div
                className="text-center lg:text-left p-6 rounded-lg bg-blue-700/30 hover:bg-blue-700/50 transition-colors duration-300 sm:col-span-2 lg:col-span-1"
                variants={statsItem}
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-3xl sm:text-4xl font-bold mb-2">
                  <AnimatedCounter value="200" />
                </h3>
                <p className="text-sm sm:text-base text-blue-100 md:text-xl">
                  Professional
                  <br />
                  Trainers
                </p>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            className="w-full h-full lg:w-1/2 lg:h-1/2 mt-8 md:mt-0 flex justify-center md:justify-end"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <motion.div
              className="w-full h-full shadow-2xl shadow-primary overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <StatsImage className="w-full h-full object-contain" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default StatsSection;
